import {
  useQuery,
  useMutation,
  useIsMutating,
  useInfiniteQuery,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  InfiniteData,
  keepPreviousData,
  UseMutationOptions,
} from '@tanstack/react-query';
import { queryClient } from '@config/query-client';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as authActions } from '@store/slice/auth-slice';
import { actions as modalActions } from '@store/slice/modal-slice';
import { ESERVER_ERROR_CODE } from '@datatype/enum';
import { AxiosError } from 'axios';
import { IApiResponse } from '@datatype/common/api-type';
import { useEffect } from 'react';
import { SERVER_CONNECTION_FAIL } from '@constant/error';

const useApiError = () => {
  const dispatch = useTypedDispatch();

  const errorHandler = (error: any) => {
    const code = error.response?.data.code;

    if (typeof error === 'string' && error === SERVER_CONNECTION_FAIL) {
      // dispatch(
      //   modalActions.showDialogModal({
      //     type: 'plain-text',
      //     data: `🛜네트워크에 문제가 생겼습니다.\n\n잠시후 다시 시도해주세요.🥲`,
      //   }),
      // );
    }

    if (code?.includes('TEMP')) return;

    if (
      [
        ESERVER_ERROR_CODE.AUTH_0051,
        ESERVER_ERROR_CODE.AUTH_0052,
        ESERVER_ERROR_CODE.AUTH_0053,
        ESERVER_ERROR_CODE.AUTH_0054,
        ESERVER_ERROR_CODE.AUTH_0055,
      ].includes(code)
    ) {
      dispatch(authActions.checkErrorCode('invalid-authentication-code'));
      return;
    }

    if ([ESERVER_ERROR_CODE.AUTH_0011].includes(code)) {
      dispatch(authActions.checkErrorCode('invalid-username-duplicate'));
      return;
    }

    if ([ESERVER_ERROR_CODE.AUTH_0012].includes(code)) {
      dispatch(authActions.checkErrorCode('invalid-username-authentication'));
      return;
    }

    if ([ESERVER_ERROR_CODE.AUTH_0013].includes(code)) {
      dispatch(authActions.checkErrorCode('invalid-email-duplicate'));
      return;
    }

    if ([ESERVER_ERROR_CODE.AUTH_0015].includes(code)) {
      dispatch(authActions.checkErrorCode('invalid-authtype-oauth'));
      return;
    }

    if ([ESERVER_ERROR_CODE.AUTH_0021].includes(code)) {
      dispatch(authActions.checkErrorCode('invalid-password-authentication'));
      return;
    }

    if ([ESERVER_ERROR_CODE.AUTH_0022, ESERVER_ERROR_CODE.AUTH_0023].includes(code)) {
      dispatch(authActions.checkErrorCode('invalid-password-modification'));
      return;
    }

    // quota·빈 전사문은 각 기능의 안내로 처리한다.
    if (code === ESERVER_ERROR_CODE.CLIENT_0005 || code === ESERVER_ERROR_CODE.CLIENT_0006) return;

    if (error.response?.status === 400) {
      dispatch(
        modalActions.showDialogModal({
          type: 'plain-text',
          data: `요청 처리에 문제가 생겼습니다.\n잠시 후 다시 시도해주세요. 🥲`,
        }),
      );
    }

    if (error.response?.status === 500) {
      dispatch(
        modalActions.showDialogModal({
          type: 'plain-text',
          data: `서버에 일시적인 문제가 생겼습니다.\n잠시 후 다시 시도해주세요. 🥲`,
        }),
      );
    }
  };

  return { errorHandler };
};

export const useApiQuery = <T = unknown, U = void>(
  queryKey: QueryKey,
  queryFn: (variables: U) => Promise<T>,
  variables: U,
  options?: Omit<UseQueryOptions<T, Error, T, QueryKey>, 'queryKey' | 'queryFn'>,
): UseQueryResult<T, Error> => {
  const { errorHandler } = useApiError();

  const apiQuery = useQuery<T, Error, T, QueryKey>({
    queryKey,
    queryFn: () => queryFn(variables),
    // staleTime: 60 * 1000,
    staleTime: 0,
    gcTime: 24 * 60 * 60 * 1000, // 24h — init-screen prefetch한 데이터를 그날 내내 캐시 hit
    ...options,
  });

  useEffect(() => {
    if (apiQuery.isError) {
      errorHandler(apiQuery.error);
    }
  }, [apiQuery.isError, apiQuery.error]);

  return apiQuery;
};

/**
 * 페이지네이션 API용 무한쿼리 헬퍼. `useApiQuery`의 InfiniteQuery 버전.
 *
 * ─────────────────────────────────────────────────────────────
 * # 캐시 저장 구조
 *
 * react-query는 응답을 페이지별로 누적 저장. 호출부는 `query.data?.pages.flatMap(p => p.data)`로 평탄화해서 사용.
 *
 * 첫 fetch (page 1 응답 받음) 직후:
 *   query.data = {
 *     pages: [
 *       { data: [item1, item2, ..., item20], page: 1, totalPage: 3, count: 20, totalCount: 42, status: 'success' },
 *     ],
 *     pageParams: [1],
 *   }
 *
 * fetchNextPage() 두 번 호출 후 (page 2, 3까지 누적):
 *   query.data = {
 *     pages: [
 *       { data: [item1 ~ item20],  page: 1, totalPage: 3, count: 20, totalCount: 42, ... },
 *       { data: [item21 ~ item40], page: 2, totalPage: 3, count: 20, totalCount: 42, ... },
 *       { data: [item41, item42],  page: 3, totalPage: 3, count: 2,  totalCount: 42, ... },
 *     ],
 *     pageParams: [1, 2, 3],
 *   }
 *
 * 호출부에서 사용:
 *   const workbookData = query.data?.pages.flatMap((p) => p.data ?? []) ?? [];
 *   // → [item1, item2, ..., item42]  (전체 42개 평탄화)
 *
 * ─────────────────────────────────────────────────────────────
 * # 자동 page 주입
 *
 * 호출부는 `page` 인자를 안 넘기고, 내부에서 `pageParam`을 자동으로 `variables.page`에 주입.
 *
 * @example
 *   useApiInfiniteQuery<IWorkbookData, IReadBookmarkRequest>(
 *     queryKey,
 *     api.getBookmarkClozeWorkbook,
 *     { clozeType, languageCode, perPage: 50 },   // page 없이 호출
 *     { enabled: isAuthenticated },
 *   );
 */
export const useApiInfiniteQuery = <T = unknown, U extends { page: number } = { page: number }>(
  queryKey: QueryKey,
  queryFn: (variables: U) => Promise<IApiResponse<T[]>>,
  variables: Omit<U, 'page'>,
  options?: Omit<
    UseInfiniteQueryOptions<IApiResponse<T[]>, Error, InfiniteData<IApiResponse<T[]>, number>, QueryKey, number>,
    'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
  >,
): UseInfiniteQueryResult<InfiniteData<IApiResponse<T[]>, number>, Error> => {
  const { errorHandler } = useApiError();

  const apiQuery = useInfiniteQuery<
    IApiResponse<T[]>,
    Error,
    InfiniteData<IApiResponse<T[]>, number>,
    QueryKey,
    number
  >({
    queryKey,
    // pageParam을 variables.page에 주입해 API 호출 — 호출부는 page 인자 신경 안 써도 됨
    // 예: 호출부가 { perPage: 50, languageCode: 'JAPANESE' } 넘기면
    //     실제 API 호출은 { perPage: 50, languageCode: 'JAPANESE', page: 1 } (첫 fetch)
    //                     { perPage: 50, languageCode: 'JAPANESE', page: 2 } (fetchNextPage)
    queryFn: ({ pageParam }) => queryFn({ ...(variables as U), page: pageParam } as U),
    initialPageParam: 1,
    // 방금 받은 응답(lastPage)을 받아 다음 페이지 번호를 결정.
    // 숫자 반환 → hasNextPage=true / undefined 반환 → hasNextPage=false (fetchNextPage 차단)
    //
    // 서버 응답 예시 (총 42개, perPage=20):
    //   page 1 응답: { page: 1, totalPage: 3, count: 20, totalCount: 42 } → 1<3 → 2 반환 (hasNextPage=true)
    //   page 2 응답: { page: 2, totalPage: 3, count: 20, totalCount: 42 } → 2<3 → 3 반환 (hasNextPage=true)
    //   page 3 응답: { page: 3, totalPage: 3, count: 2,  totalCount: 42 } → 3<3 → undefined (hasNextPage=false)
    getNextPageParam: (lastPage) => {
      const page = lastPage.page ?? 1;
      const totalPage = lastPage.totalPage ?? 1;
      return page < totalPage ? page + 1 : undefined;
    },
    staleTime: 0,
    gcTime: 24 * 60 * 60 * 1000, // 24h — init-screen prefetch한 데이터를 그날 내내 캐시 hit
    ...options,
  });

  useEffect(() => {
    if (apiQuery.isError) {
      errorHandler(apiQuery.error);
    }
  }, [apiQuery.isError, apiQuery.error]);

  return apiQuery;
};

/**
 * @description mutation 공통함수
 * @param extra.dedupInFlight  true면 같은 mutationKey의 mutation이 진행 중일 때 mutate 호출 무시 (글로벌 in-flight dedup).
 *                              여러 컴포넌트/훅 인스턴스에서 동시에 호출돼도 한 번만 실제 요청됨. mutationKey 필수.
 */
export const useApiMutation = <DATA, VAR, CTX = void>(
  api: (variables: VAR) => Promise<DATA>,
  option?: UseMutationOptions<DATA, AxiosError<any>, VAR, CTX>,
  extra?: { dedupInFlight?: boolean },
) => {
  const { errorHandler } = useApiError();
  const { onError: onErrorOption, mutationKey, ...restOption } = option ?? {};

  const mutation = useMutation<DATA, AxiosError<any>, VAR, CTX>({
    mutationKey,
    mutationFn: api,
    onMutate: (variables) => undefined as CTX,
    onError: (error, variables, context) => {
      errorHandler(error);
      onErrorOption?.(error, variables, context);
    },

    // TODO: api error dialog modal 처리
    onSuccess: (data, variables, context) => {},
    onSettled: () => {},
    ...restOption,
  });

  // 글로벌 in-flight 카운트 — 같은 mutationKey로 진행 중인 mutation의 개수
  // useIsMutating은 QueryClient 전역 상태를 구독하므로 다른 컴포넌트/훅 인스턴스에서 호출된 mutation도 함께 집계됨
  const inflightCount = useIsMutating(mutationKey ? { mutationKey } : undefined);

  // mutate wrapper — dedupInFlight 옵션이 켜져 있고 같은 키의 mutation이 이미 진행 중이면 호출 무시
  // RN 자동 리스너처럼 외부에서 같은 이벤트를 짧은 시간 내 여러 번 발화시켜도 실제 요청은 1번만 나감
  const safeMutate: typeof mutation.mutate = (variables, options) => {
    if (extra?.dedupInFlight && mutationKey && inflightCount > 0) {
      console.warn('=== Skip duplicate mutation (in-flight) ===', mutationKey);
      return;
    }
    return mutation.mutate(variables, options);
  };

  return { ...mutation, mutate: safeMutate };
};

/**
 * 쿼리 데이터의 특정 아이템을 통째로 교체 (서버 응답 동기화용)
 * @param queryKey 쿼리 키
 * @param itemKey 교체할 아이템의 키
 * @param newItem 새로운 아이템 (전체)
 * @param listKey data 객체 안에서 교체할 목록의 키
 */
export const replaceQueryDataItem = <T>(
  queryKey: any,
  listKey: string | undefined,
  itemKey: string,
  newItem: T,
) => {
  queryClient.setQueryData<IApiResponse<T[] | T | Record<string, unknown>>>(queryKey, (prev) => {
    if (!prev || !prev.data) return prev;

    const { data: prevData } = prev;

    if (listKey) {
      const data = prevData as Record<string, unknown>;
      const items = data[listKey];
      if (!Array.isArray(items)) return prev;
      return {
        ...prev,
        data: {
          ...data,
          [listKey]: items.map((item) => item.key === itemKey ? newItem : item),
        },
      };
    }

    if (Array.isArray(prevData)) {
      const updatedData = prevData.map((prevItem) => ((prevItem as any).key === itemKey ? newItem : prevItem));
      return { ...prev, data: updatedData };
    }

    return {
      ...prev,
      data: newItem,
    };
  });
};

/**
 * 쿼리 데이터의 특정 아이템을 부분 병합 (낙관적 업데이트용)
 * @param queryKey 쿼리 키
 * @param itemKey 병합할 아이템의 키
 * @param patch 병합할 필드들
 */
export const mergeQueryDataItem = <T>(queryKey: any, itemKey: string, patch: Partial<T>) => {
  queryClient.setQueryData<IApiResponse<T[]> | IApiResponse<T>>(queryKey, (prev) => {
    if (!prev || !prev.data) return prev;

    const { data: prevData } = prev;

    if (Array.isArray(prevData)) {
      const updatedData = prevData.map((prevItem) =>
        (prevItem as any).key === itemKey ? { ...(prevItem as any), ...patch } : prevItem,
      );
      return { ...prev, data: updatedData };
    }

    if ((prevData as any).key === itemKey) {
      return { ...prev, data: { ...(prevData as any), ...patch } };
    }
    return prev;
  });
};

/**
 * 쿼리 데이터 배열에 새 아이템을 추가 (서버 생성 응답 동기화용)
 * @param queryKey 쿼리 키
 * @param itemKey 추가할 아이템의 키
 * @param newItem 새 아이템
 * @param listKey data 객체 안에서 추가할 목록의 키
 * @param position 추가 위치. 기본값은 기존처럼 배열의 앞쪽이다.
 */
export const appendQueryDataItem = <T>(
  queryKey: any,
  listKey: string | undefined,
  itemKey: string,
  newItem: T,
  position: 'start' | 'end' = 'start',
) => {
  queryClient.setQueryData<IApiResponse<T[] | Record<string, unknown>>>(queryKey, (prev) => {
    if (!prev || !prev.data) return prev;

    const items = listKey ? (prev.data as Record<string, unknown>)[listKey] : prev.data;
    if (!Array.isArray(items)) return prev;
    const exists = items.some((prevItem) => prevItem.key === itemKey);
    if (exists) return prev;

    const updatedData = position === 'end' ? [...items, newItem] : [newItem, ...items];
    if (listKey) {
      return { ...prev, data: { ...prev.data, [listKey]: updatedData } };
    }
    return { ...prev, data: updatedData };
  });
};

/**
 * 삭제 성공 후 쿼리 목록에서 해당 아이템을 제거한다.
 * @param queryKey 쿼리 키
 * @param listKey data 객체 안의 목록 키. 일반 배열이면 undefined
 * @param itemKey 삭제할 아이템의 키
 */
export const removeQueryDataItem = (queryKey: any, listKey: string | undefined, itemKey: string) => {
  queryClient.setQueryData<IApiResponse<unknown[] | Record<string, unknown>>>(queryKey, (prev) => {
    if (!prev || !prev.data) return prev;

    const items = listKey ? (prev.data as Record<string, unknown>)[listKey] : prev.data;
    if (!Array.isArray(items)) return prev;
    const updatedData = items.filter((item) => item.key !== itemKey);
    if (listKey) {
      return { ...prev, data: { ...prev.data, [listKey]: updatedData } };
    }
    return { ...prev, data: updatedData };
  });
};

/**
 * InfiniteQuery의 모든 페이지를 순회하며 특정 아이템을 통째로 교체 (서버 응답 동기화용)
 * @param queryKey 쿼리 키
 * @param itemKey 교체할 아이템의 키
 * @param newItem 새로운 아이템 (전체)
 */
export const replaceInfiniteQueryDataItem = <T>(queryKey: any, itemKey: string, newItem: T) => {
  queryClient.setQueryData<InfiniteData<IApiResponse<T[]>, number>>(queryKey, (prev) => {
    if (!prev) return prev;

    const updatedPages = prev.pages.map((prevPage) => {
      if (!prevPage?.data) return prevPage;
      const updatedData = prevPage.data.map((prevItem) => ((prevItem as any).key === itemKey ? newItem : prevItem));
      return { ...prevPage, data: updatedData };
    });

    return { ...prev, pages: updatedPages };
  });
};

/**
 * Infinite 목록에 항목을 추가하고 페이지·개수를 보정한다. 기존 항목은 교체한다.
 * 부분 로딩·미로딩 시 해당 목록만 재조회한다. 페이지 크기는 queryKey의 마지막 숫자를 사용한다.
 */
export const appendInfiniteQueryDataItem = <T extends { key: string }>(
  queryKey: QueryKey,
  itemKey: string,
  newItem: T,
  perPage = queryKey[queryKey.length - 1] as number,
): Promise<void> => {
  let needsRefetch = true;
  queryClient.setQueryData<InfiniteData<IApiResponse<T[]>, number>>(queryKey, (prev) => {
    if (!prev?.pages.length) return prev;

    const lastPage = prev.pages[prev.pages.length - 1];
    needsRefetch = (lastPage.page ?? 1) < (lastPage.totalPage ?? 1);
    const items = prev.pages.flatMap((page) => page.data ?? []);
    const exists = items.some((item) => item.key === itemKey);
    const updatedItems = exists ? items.map((item) => (item.key === itemKey ? newItem : item)) : [newItem, ...items];
    const totalCount = (prev.pages[0].totalCount ?? items.length) + (exists ? 0 : 1);
    const totalPage = Math.ceil(totalCount / perPage);
    const pageCount = Math.max(1, Math.ceil(updatedItems.length / perPage));
    const updatedPages = Array.from({ length: pageCount }, (_, index) => {
      const updatedData = updatedItems.slice(index * perPage, (index + 1) * perPage);
      return {
        ...(prev.pages[index] ?? lastPage),
        data: updatedData,
        count: updatedData.length,
        page: index + 1,
        perPage,
        totalCount,
        totalPage,
      };
    });

    return { ...prev, pages: updatedPages, pageParams: updatedPages.map((page) => page.page) };
  });
  return needsRefetch
    ? queryClient.invalidateQueries({ queryKey, exact: true })
    : Promise.resolve();
};

/**
 * Infinite 목록에서 key에 해당하는 항목을 제거하고 페이지·개수를 보정한다.
 * 부분 로딩·미로딩 시 해당 목록만 재조회한다. 페이지 크기는 queryKey의 마지막 숫자를 사용한다.
 */
export const removeInfiniteQueryDataItem = <T extends { key: string }>(
  queryKey: QueryKey,
  itemKey: string,
): Promise<void> => {
  const perPage = queryKey[queryKey.length - 1] as number;
  let needsRefetch = true;
  queryClient.setQueryData<InfiniteData<IApiResponse<T[]>, number>>(queryKey, (prev) => {
    if (!prev?.pages.length) return prev;

    const lastPage = prev.pages[prev.pages.length - 1];
    needsRefetch = (lastPage.page ?? 1) < (lastPage.totalPage ?? 1);
    const items = prev.pages.flatMap((page) => page.data ?? []);
    const updatedItems = items.filter((item) => item.key !== itemKey);
    const totalCount = Math.max(0, (prev.pages[0].totalCount ?? items.length) - (items.length - updatedItems.length));
    const totalPage = Math.ceil(totalCount / perPage);
    const pageCount = Math.max(1, Math.ceil(updatedItems.length / perPage));
    const updatedPages = Array.from({ length: pageCount }, (_, index) => {
      const updatedData = updatedItems.slice(index * perPage, (index + 1) * perPage);
      return {
        ...(prev.pages[index] ?? lastPage),
        data: updatedData,
        count: updatedData.length,
        page: index + 1,
        perPage,
        totalCount,
        totalPage,
      };
    });

    return { ...prev, pages: updatedPages, pageParams: updatedPages.map((page) => page.page) };
  });
  return needsRefetch
    ? queryClient.invalidateQueries({ queryKey, exact: true })
    : Promise.resolve();
};

/**
 * InfiniteQuery의 모든 페이지를 순회하며 특정 아이템을 부분 병합 (낙관적 업데이트용)
 * @param queryKey 쿼리 키
 * @param itemKey 병합할 아이템의 키
 * @param patch 병합할 필드들
 */
export const mergeInfiniteQueryDataItem = <T>(queryKey: any, itemKey: string, patch: Partial<T>) => {
  queryClient.setQueryData<InfiniteData<IApiResponse<T[]>, number>>(queryKey, (prev) => {
    if (!prev) return prev;

    const updatedPages = prev.pages.map((prevPage) => {
      if (!prevPage?.data) return prevPage;
      const updatedData = prevPage.data.map((prevItem) =>
        (prevItem as any).key === itemKey ? { ...(prevItem as any), ...patch } : prevItem,
      );
      return { ...prevPage, data: updatedData };
    });

    return { ...prev, pages: updatedPages };
  });
};

/**
 * 여러 쿼리 키의 현재 캐시 데이터를 스냅샷으로 저장 (롤백용)
 */
export const snapshotQueryData = (queryKeys: any[]) => {
  return queryKeys.map((queryKey) => ({ queryKey, data: queryClient.getQueryData(queryKey) }));
};

/**
 * 스냅샷을 사용해 쿼리 데이터를 롤백
 */
export const rollbackQueryData = (snapshots: { queryKey: any; data: unknown }[]) => {
  snapshots.forEach(({ queryKey, data }) => {
    queryClient.setQueryData(queryKey, data);
  });
};
